// Identity — SSO providers a developer signs in with (step-ca OIDC), plus the
// fleet CA they enroll against.
//
// Presentation adopts the fleet-management-agate card language (matching the
// Fleet page): a serif page head, one card kit (`@/components/Card` + agate
// primitives), semantic tokens throughout. The data + safety layer is ours —
// providers come live from fleetd (identityctl → `just add|remove`), the CA
// summary from /api/fleet; each mutation is a tracked job (step-ca re-reads its
// config + restarts), polled to completion. A hard API error falls back to a
// single REPRESENTATIVE provider — always labelled, never rendered as live.
import { useCallback, useEffect, useMemo, useState } from "react";
import { App as AntApp, Alert, Button, Collapse, Form, Input, Modal, Popconfirm, Segmented, Space, Spin, Tag, Typography, theme } from "antd";
import { Plus, Pencil, Trash2, ShieldCheck, KeyRound, CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import Card from "@/components/Card";
import { SectionTitle, EmptyNote } from "@/components/agate";
import PageHeader from "@/components/PageHeader";
import { fleetApi, type Provider, type ProviderInput } from "@/lib/api/fleet";

const { Text } = Typography;
const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

type Kind = "entra" | "google";
const PREREQS: Record<Kind, string[]> = {
  entra: [
    "App registration in the tenant your users sign in to (single-tenant, AzureADMyOrg).",
    "Public client / PKCE, NO client secret (a secret makes Entra return invalid_client).",
    "Redirect URIs under “Mobile and desktop applications”: http://127.0.0.1:10000 (preferred), https://localhost.",
    "API permission: Microsoft Graph User.Read (delegated) — user-consentable.",
    "Record the Client ID and Tenant ID.",
  ],
  google: [
    "OAuth 2.0 Client ID in Google Cloud Console (type: Desktop app).",
    "No client secret required for the loopback/PKCE flow.",
    "Authorized redirect: http://127.0.0.1:10000.",
    "Restrict to your Workspace domain; record the Client ID.",
  ],
};

// Synthetic placeholder, shown only when the API is unreachable. NEVER put real
// tenant GUIDs, client IDs, or employee emails here — this string ships in the
// browser bundle and would render as the customer's own IdP config.
const FALLBACK: Provider[] = [
  { handle: "entra", name: "Azure-Entra-OIDC", kind: "entra",
    issuer: "https://login.microsoftonline.com/00000000-0000-0000-0000-000000000000/v2.0",
    client_id: "11111111-1111-1111-1111-111111111111", admins: "admin@example.com",
    domains: "example.com", default_dur: "8h", max_dur: "24h", has_client_secret: false, registered: true },
];

function tenantOf(issuer: string) {
  const m = issuer.match(/login\.microsoftonline\.com\/([^/]+)/);
  return m ? m[1] : "";
}

type CAHead = {
  ca?: { name: string; host: string; endpoint: string; fp: string };
  sso?: { provider: string; tenant: string; provisioner: string; protocol: string };
  connect?: { bootstrap_url: string; ssh_host: string; login: string; client_hint: string; access_note: string };
};

export default function Identity() {
  const { token } = theme.useToken();
  const { message } = AntApp.useApp();
  const [providers, setProviders] = useState<Provider[] | null>(null);
  // loading → first fetch in flight; ok → live; error → API unreachable (falls
  // back to a labelled representative provider); auth → session expired.
  const [status, setStatus] = useState<"loading" | "ok" | "error" | "auth" | "forbidden">("loading");
  const [editing, setEditing] = useState<{ mode: "add" } | { mode: "edit"; provider: Provider } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setProviders(await fleetApi.providers());
      setStatus("ok");
    } catch (e) {
      // Keep last-good data on a transient error. Classify the first-load failure:
      // 401 = session expired (sign-in); 403 = signed in but not admin (this page is
      // admin-only) — show a permission message, NEVER a fabricated provider; anything
      // else = the backend is genuinely unreachable.
      const msg = String((e as Error)?.message);
      setStatus((s) => (s === "ok" ? "ok"
        : /\b401\b/.test(msg) ? "auth"
        : /\b403\b/.test(msg) ? "forbidden"
        : "error"));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // CA summary + developer enroll string (ca/sso/connect blocks from /api/fleet).
  const [ca, setCa] = useState<CAHead | null>(null);
  useEffect(() => {
    const tok = localStorage.getItem("bluerock.token");
    fetch("/api/fleet", { cache: "no-store", headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCa({ ca: d.ca, sso: d.sso, connect: d.connect }))
      .catch(() => { /* CA panel is best-effort; providers list is the page's core */ });
  }, []);

  const isFallback = status === "error";
  const rows = providers ?? (isFallback ? FALLBACK : []);

  const runJob = useCallback(async (start: Promise<{ job_id: string }>, label: string) => {
    setBusy(label);
    try {
      const { job_id } = await start;
      let settled = false;
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const j = await fleetApi.job(job_id).catch(() => null);
        if (j && (j.state === "success" || j.state === "failed")) {
          settled = true;
          const tail = (j.log || []).slice(-1)[0] || "see logs";
          if (j.result === "rolled_back") {
            // Safe outcome: the change was reverted and the previous sign-in
            // config is still live — surface it as a warning, not a failure.
            message.warning(`${label} was reverted — previous sign-in config is unchanged and still active.`);
          } else if (j.state === "success") {
            message.success(`${label} — done`);
          } else {
            message.error(`${label} failed: ${tail}`);
          }
          break;
        }
      }
      if (!settled) {
        // Never leave the operator staring at a stopped spinner: say the job is
        // still running and where to look, rather than silently falling through.
        message.warning(`${label} is still running after 90s — check the job log; refresh to see the final state.`);
      }
      await load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : `${label} failed`);
    } finally {
      setBusy(null);
    }
  }, [load, message]);

  const remove = (p: Provider) => runJob(fleetApi.removeProvider(p.handle), `Remove ${p.name}`);

  // Session expired (401, no data yet) — a sign-in prompt, never fabricated providers.
  if (providers === null && status === "auth") {
    return (
      <>
        <PageHead onAdd={() => setEditing({ mode: "add" })} disabled={!!busy} />
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <span className="text-sm text-text-muted">Your session expired. Sign in again to manage identity providers.</span>
          <Button type="primary" onClick={() => { window.location.href = "/login"; }}>Sign in</Button>
        </Card>
      </>
    );
  }

  if (providers === null && status === "forbidden") {
    // Signed in, but not an admin — identity management is admin-only. Show the real
    // reason; never the synthetic FALLBACK provider or a false "API unreachable".
    return (
      <>
        <PageHead onAdd={() => setEditing({ mode: "add" })} disabled />
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <span className="text-sm text-text-muted">Managing identity providers requires the admin role. You’re signed in without it, so this page is read-restricted.</span>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHead onAdd={() => setEditing({ mode: "add" })} disabled={!!busy || isFallback} />

      {isFallback && (
        <Alert
          type="warning"
          showIcon
          icon={<AlertTriangle size={16} />}
          className="mb-4"
          message="Showing a representative provider — the fleet API is unreachable"
          description="The provider below is illustrative, not your live sign-in config. Don’t add, edit, or remove providers until the connection is restored."
          action={<Button size="small" onClick={() => load()}>Retry</Button>}
        />
      )}

      {/* Certificate Authority — the CA the providers register on, plus the
          enroll string an operator hands a developer. Matches the Fleet host
          card: header row of identity, a mono detail grid, a divided sub-block. */}
      <SectionTitle hint="The step-ca that signs short-lived SSH certs once a developer completes SSO. Providers below register on it.">Certificate authority</SectionTitle>
      {ca?.ca ? (
        <Card className="mb-6 p-0">
          <div className="px-5 pt-4 pb-3">
            <div className="flex flex-wrap items-center gap-2">
              <ShieldCheck size={16} className="text-accent" />
              <span style={{ fontFamily: MONO }} className="text-[14px] font-semibold text-text-primary">{ca.ca.name}</span>
              <Tag color="green" style={{ fontFamily: MONO }}>step-ca</Tag>
              {ca.sso?.provider && <Tag style={{ fontFamily: MONO }}>{ca.sso.provider}</Tag>}
            </div>
            <div style={{ fontFamily: MONO }} className="mt-2.5 grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs text-text-muted sm:grid-cols-2">
              <span className="truncate">endpoint <span className="text-text-secondary">{ca.ca.endpoint}</span></span>
              <span className="truncate">provisioner <span className="text-text-secondary">{ca.sso?.provisioner || "—"}</span></span>
              <span className="inline-flex items-center gap-1 truncate">
                fingerprint
                <Text copyable={{ text: ca.ca.fp }} style={{ fontFamily: MONO, color: token.colorTextSecondary }}>{ca.ca.fp.slice(0, 16)}…</Text>
              </span>
              <span className="truncate">protocol <span className="text-text-secondary">{ca.sso?.protocol || "—"}</span></span>
            </div>
          </div>
          {ca.connect?.bootstrap_url && (
            <div className="border-t border-border px-5 py-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">Onboard a developer</div>
              <div className="flex items-center gap-2">
                <span style={{ fontFamily: MONO }} className="min-w-0 flex-1 truncate rounded-lg border border-border bg-surface-raised px-2.5 py-1.5 text-xs text-text-secondary">{ca.connect.bootstrap_url}</span>
                <Text copyable={{ text: ca.connect.bootstrap_url }} />
              </div>
              <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
                The cobalt enrollment string. A developer runs
                <Step>enroll-cert</Step><ArrowRight size={11} className="text-text-muted" />
                <Step>login</Step><ArrowRight size={11} className="text-text-muted" />
                <Step>up</Step>
                <span>— the full walkthrough is on the Fleet page’s Connect button.</span>
              </p>
            </div>
          )}
        </Card>
      ) : (
        <Card className="mb-6 flex items-center gap-3 py-6">
          {status === "loading"
            ? <><Spin size="small" /><span className="text-sm text-text-muted">Loading the fleet CA…</span></>
            : <EmptyNote>Fleet CA summary is unavailable right now.</EmptyNote>}
        </Card>
      )}

      {/* Sign-in providers — one agate card per provider, matching the CA panel. */}
      <SectionTitle hint="OIDC providers a developer signs in with to get an SSH cert. Editing re-registers on the CA and restarts step-ca.">Sign-in providers</SectionTitle>
      {status === "loading" && providers === null ? (
        <Card className="flex items-center justify-center py-12"><Spin /><span className="ml-3 text-sm text-text-muted">Loading providers…</span></Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <KeyRound size={22} className="text-text-muted" />
          <EmptyNote>No identity providers yet — add one to enable SSO sign-in.</EmptyNote>
          <Button type="primary" icon={<Plus size={15} />} disabled={!!busy} onClick={() => setEditing({ mode: "add" })}>Add provider</Button>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((p) => (
            <Card key={p.handle} className="p-0">
              <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <KeyRound size={15} className="text-accent" />
                    <span style={{ fontFamily: MONO }} className="text-[14px] font-semibold text-text-primary">{p.name}</span>
                    <Tag style={{ fontFamily: MONO }}>{p.kind}</Tag>
                    {p.registered
                      ? <Tag color="green"><CheckCircle2 size={11} style={{ display: "inline", marginRight: 3, verticalAlign: "-1px" }} />registered</Tag>
                      : <Tag>not registered</Tag>}
                  </div>
                  <div style={{ fontFamily: MONO }} className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-text-muted sm:grid-cols-2">
                    <span className="truncate">client_id <span className="text-text-secondary">{p.client_id || "—"}</span></span>
                    <span className="truncate">domains <span className="text-text-secondary">{p.domains || "—"}</span></span>
                    <span className="truncate">tenant <span className="text-text-secondary">{tenantOf(p.issuer) || "—"}</span></span>
                    <span className="truncate">certs <span className="text-text-secondary">{p.default_dur || "8h"} / {p.max_dur || "24h"}</span></span>
                  </div>
                </div>
                <Space>
                  {p.external ? (
                    // Configured directly in the CA (no console-managed env). We can't
                    // see its secret, and editing/removing it restarts step-ca, so keep
                    // it read-only here — manage it on the host if it must change.
                    <Tag color="default">Configured in the CA · read-only</Tag>
                  ) : (
                    <>
                      <Button size="small" icon={<Pencil size={13} />} disabled={!!busy || isFallback} onClick={() => setEditing({ mode: "edit", provider: p })}>Edit</Button>
                      <Popconfirm title={`Remove ${p.name}?`} description="Developers using it lose SSO sign-in until re-added." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(p)} disabled={isFallback}>
                        <Button size="small" danger icon={<Trash2 size={13} />} disabled={!!busy || isFallback}>Remove</Button>
                      </Popconfirm>
                    </>
                  )}
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs text-text-muted">
        {busy ? <><Loader2 size={13} className="animate-spin" style={{ color: token.colorPrimary }} /> {busy}…</>
          : <><ShieldCheck size={13} className="text-accent" /> Changes re-register the provider on the CA and restart step-ca (a few seconds).</>}
      </div>

      <ProviderModal
        target={editing}
        onCancel={() => setEditing(null)}
        onSubmit={async (input) => {
          const isEdit = editing?.mode === "edit";
          const label = (isEdit ? "Update " : "Add ") + (input.name || input.handle);
          setEditing(null);
          if (isEdit) await runJob(fleetApi.updateProvider(input.handle, input), label);
          else await runJob(fleetApi.addProvider(input), label);
        }}
      />
    </>
  );
}

function PageHead({ onAdd, disabled }: { onAdd: () => void; disabled: boolean }) {
  return (
    <div className="mb-5">
      <PageHeader
        title="Identity providers"
        subtitle="SSO providers developers sign in with to get an SSH cert. Backed by the fleet CA (step-ca)."
        actions={<Button type="primary" icon={<Plus size={15} />} disabled={disabled} onClick={onAdd}>Add provider</Button>}
      />
    </div>
  );
}

// Step — a single mono command chip in the enroll walkthrough.
function Step({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontFamily: MONO }} className="rounded border border-border bg-surface-raised px-1.5 py-0.5 text-[11px] text-text-secondary">{children}</span>
  );
}

function ProviderModal({
  target, onCancel, onSubmit,
}: {
  target: { mode: "add" } | { mode: "edit"; provider: Provider } | null;
  onCancel: () => void;
  onSubmit: (input: ProviderInput & { handle: string }) => Promise<void>;
}) {
  const [form] = Form.useForm();
  const [kind, setKind] = useState<Kind>("entra");
  const [saving, setSaving] = useState(false);
  const isEdit = target?.mode === "edit";

  useEffect(() => {
    if (!target) return;
    if (target.mode === "edit") {
      const p = target.provider;
      setKind(p.kind === "google" ? "google" : "entra");
      form.setFieldsValue({
        handle: p.handle, name: p.name, tenant: tenantOf(p.issuer),
        client_id: p.client_id, admins: p.admins, domains: p.domains,
        default_dur: p.default_dur || "8h", max_dur: p.max_dur || "24h",
      });
    } else {
      setKind("entra");
      form.resetFields();
      form.setFieldsValue({ default_dur: "8h", max_dur: "24h", name: "Azure-Entra-OIDC", handle: "entra" });
    }
  }, [target, form]);

  const values = Form.useWatch([], form);
  const issuer = useMemo(() => {
    if (kind === "google") return "https://accounts.google.com";
    return `https://login.microsoftonline.com/${values?.tenant || "<TENANT_ID>"}/v2.0`;
  }, [kind, values]);
  const envPreview = useMemo(() => [
    `PROVISIONER_NAME=${values?.name || "<name>"}`,
    `ISSUER_URL=${issuer}`,
    `CLIENT_ID=${values?.client_id || "<client id>"}`,
    `CLIENT_SECRET=${kind === "entra" ? "" : (values?.client_secret ? "••••" : "")}`,
    `ADMINS=${values?.admins || "<admin@domain>"}`,
    `BLUEDEV_DOMAINS=${values?.domains || "<domain>"}`,
    `DEFAULT_SSH_CERT_DURATION=${values?.default_dur || "8h"}`,
    `MAX_SSH_CERT_DURATION=${values?.max_dur || "24h"}`,
  ].join("\n"), [values, issuer, kind]);

  return (
    <Modal open={!!target} onCancel={onCancel} width={620} title={isEdit ? "Edit identity provider" : "Add identity provider"}
      okText={isEdit ? "Save changes" : "Add provider"} okButtonProps={{ loading: saving }}
      onOk={async () => {
        try {
          const v = await form.validateFields();
          setSaving(true);
          // Google has no Tenant field, so the backend can't derive an issuer from a
          // tenant — send the fixed Google issuer explicitly, else add/modify 400s.
          const extra = kind === "google" ? { issuer_url: "https://accounts.google.com" } : {};
          await onSubmit({ ...v, kind, ...extra } as ProviderInput & { handle: string });
        } catch { /* validation errors surface inline */ } finally { setSaving(false); }
      }}
      destroyOnHidden>
      <div className="mb-3">
        <Segmented value={kind} onChange={(v) => setKind(v as Kind)} disabled={isEdit}
          options={[{ label: "Microsoft Entra", value: "entra" }, { label: "Google", value: "google" }]} />
      </div>
      <Form form={form} layout="vertical" requiredMark={false}>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item label="Handle" name="handle" tooltip="Internal id (the providers/<handle>.env file); lowercase."
            rules={[{ required: true, pattern: /^[a-z0-9][a-z0-9_-]{0,31}$/, message: "a-z 0-9 _ - , ≤32" }]}>
            <Input placeholder="entra" disabled={isEdit} />
          </Form.Item>
          <Form.Item label="Display name" name="name" tooltip="Shown in step-ca (PROVISIONER_NAME)." rules={[{ required: true }]}>
            <Input placeholder="Azure-Entra-OIDC" />
          </Form.Item>
        </div>
        {kind === "entra" && (
          <Form.Item label="Tenant ID" name="tenant" rules={[{ required: true, message: "tenant id required" }]}>
            <Input placeholder="00000000-0000-0000-0000-000000000000" />
          </Form.Item>
        )}
        <Form.Item label="Client ID" name="client_id" rules={[{ required: true }]}>
          <Input placeholder={kind === "entra" ? "application (client) id" : "…apps.googleusercontent.com"} />
        </Form.Item>
        {kind === "google" && (
          <Form.Item label="Client secret" name="client_secret" tooltip="Optional for loopback/PKCE.">
            <Input.Password placeholder="optional" autoComplete="off" />
          </Form.Item>
        )}
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item label="Admin emails" name="admins" tooltip="Comma-separated; admins get requested principals verbatim. Required by the CA."
            rules={[{ required: true, message: "at least one admin email" }]}>
            <Input placeholder="you@bluerock.io" />
          </Form.Item>
          <Form.Item label="Allowed domains" name="domains" tooltip="Comma-separated; who may sign in.">
            <Input placeholder="bluerock.io" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item label="Default cert TTL" name="default_dur"><Input placeholder="8h" /></Form.Item>
          <Form.Item label="Max cert TTL" name="max_dur"><Input placeholder="24h" /></Form.Item>
        </div>
      </Form>

      <Collapse ghost items={[
        { key: "pre", label: <span className="text-xs text-text-secondary">Prerequisites ({kind === "entra" ? "Entra" : "Google"})</span>,
          children: <ul className="ml-4 list-disc text-xs text-text-muted">{PREREQS[kind].map((s, i) => <li key={i} className="mb-1">{s}</li>)}</ul> },
        { key: "env", label: <span className="text-xs text-text-secondary">providers/{values?.handle || "handle"}.env preview</span>,
          children: <pre className="overflow-x-auto rounded-lg border border-border bg-surface-raised px-3 py-2 text-[11.5px] text-text-secondary" style={{ fontFamily: MONO }}>{envPreview}</pre> },
      ]} />
    </Modal>
  );
}
